import React, { useCallback, useEffect } from 'react';
import Card from 'react-bootstrap/Card';
import PropTypes from 'prop-types';
// components
import { Background } from 'layout';
import { Button, Loader } from 'ui';
import styles from './IdSessionTimeout.module.css';
import { setButtonVariant } from 'utils/utilities';
// assets
import SessionTimeout from 'assets/images/session_timeout.svg';
import { useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { selectTheme } from 'store/common/selectors/profile';
import { useGovtIdFlowChrome } from 'features/govtId/hooks/useGovtIdFlowChrome';
import { VERIFICATION_FLOWS, REAUTHENTICATE_ROUTES } from 'utils/constants';
import { trackAction } from 'features/govtId/v2/analytics/track';
import { ACTIONS } from 'features/govtId/v2/analytics/events';

function IdSessionTimeout({ verificationFlow }) {
    const navigate = useNavigate();
    const location = useLocation();
    const theme = useSelector(selectTheme);

    const { isFlowResolved, retryPath, resetCaptureState } = useGovtIdFlowChrome();

    const isReauthenticate = verificationFlow === VERIFICATION_FLOWS.REAUTHENTICATE;
    // Reauthenticate restarts at its own selfie step, so it needs nothing from the capture flow.
    const isReady = isReauthenticate || isFlowResolved;

    const handleRouting = useCallback(() => {
        navigate(isReauthenticate ? REAUTHENTICATE_ROUTES.CAPTURE_SELFIE : retryPath);
    }, [navigate, isReauthenticate, retryPath]);

    useEffect(() => {
        if (!isReady) return;

        if (!location.state?.sessionTimeout) {
            handleRouting();
        }
    }, [location, isReady, handleRouting]);

    const handleRestartSession = () => {
        trackAction(ACTIONS.TRY_AGAIN_TAPPED);
        resetCaptureState();
        handleRouting();
    };

    if (!isReady) {
        return (
            <Background className={styles.sessionWrapper}>
                <Card.Body>
                    <Loader />
                </Card.Body>
            </Background>
        );
    }

    return (
        <Background className={styles.sessionWrapper}>
            <Card.Header className={styles.cardHeader}>Session Time Out</Card.Header>
            <Card.Body className={styles.cardBody}>
                <div className={styles.messageBox}>
                    <p className={styles.displayMessage}>
                        Please restart the session to complete your verification
                    </p>
                </div>

                <div className={styles.animationContainer}>
                    <img
                        src={SessionTimeout}
                        alt="Session timeout illustration"
                        className={styles.timeoutImage}
                    />
                </div>
                <div className={styles.btnContainer}>
                    <Button
                        block
                        className={styles.nextStepBtn}
                        variant={setButtonVariant(theme?.button?.variant)}
                        onClick={handleRestartSession}
                    >
                        Restart
                    </Button>
                </div>
            </Card.Body>
        </Background>
    );
}

IdSessionTimeout.propTypes = {
    verificationFlow: PropTypes.string,
};

export default IdSessionTimeout;
